<template>
  <div v-if="visible" class="competencies-modal-overlay" @click.self="closeModal" @click.stop @keydown.esc="closeModal">
    <div class="competencies-modal-dialog" @click.stop>
      <div class="competencies-modal-header">
        <h3 class="competencies-modal-title">
          {{ userName }} -- {{ $t('general.competencies') }} ({{ competencies.length }})
        </h3>
        <button class="competencies-modal-close" type="button" @click="closeModal" @click.stop>
          <span>×</span>
        </button>
      </div>

      <div class="competencies-modal-body">
        <div v-if="loading" class="text-center p-4">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only"> {{ $t('general.loading') }} ...</span>
          </div>
          <p class="mt-2"> {{ $t('general.competencies_loading') }} ...</p>
        </div>

        <div v-else-if="competencies.length === 0" class="text-center p-4">
          <i class="uil-inbox" style="font-size: 3rem; color: #ccc;"></i>
          <h5 class="mt-3"> {{ $t('general.no_have_competencies_assigned') }}</h5>
          <p class="text-muted"> {{ $t('general.no_have_competencies_assigned_info') }} </p>
        </div>

        <div v-else class="competencies-table">
          <table class="table">
            <thead>
            <tr>
              <th> {{ $t('general.competency_definition') }}</th>
              <th> {{ $t('general.situation') }}</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="competency in competencies" :key="competency.id">
              <td class="competency-name">
                <span class="competency-title">{{ competency.name }}</span>
              </td>
              <td class="competency-status">
                  <span :class="getTypeBadgeClass(competency.status)" class="type-badge">
                    {{ getTypeText(competency.status) }}
                  </span>
              </td>
              <td class="competency-actions">
                <button :title="$t('general.delete')" class="delete-btn" @click="deleteCompetency(competency)">
                  <i class="uil-trash-alt"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="competencies-modal-footer">
        <button class="add-competency-btn" @click="addCompetency">
          <i class="uil-plus mr-1"></i> + {{ $t('general.define_competency') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { showError, showSuccess } from '@/utils/alertUtils.js';
import { confirmDialogDelete } from "@/utils/confirmDialog";

export default {
  name: "UserCompetenciesModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [Number, String],
      required: true
    },
    userName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      competencies: []
    };
  },

  watch: {
    visible(value) {
      if (value && this.userId) {
        this.loadCompetencies();
      }
    }
  },
  methods: {
    async loadCompetencies() {
      this.loading = true;
      try {
        const response = await ApiService.get(`/api/user/competencies/${this.userId}`);
        this.competencies = response.data?.data || [];
      } catch (error) {
        console.error('Kullanıcı yetkinlikleri yüklenirken hata:', error);
        showError(this.$t('general.error_occurred_while_loading_competencies'),3000);
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.$emit('close');
    },

    getTypeBadgeClass(status) {
      switch (status) {
        case 'approved':
          return 'type-badge-approved';
        case 'pending':
          return 'type-badge-pending';
        case 'rejected':
          return 'type-badge-rejected';
        default:
          return 'type-badge-default';
      }
    },

    getTypeText(status) {
      switch (status) {
        case 'approved':
          return this.$t('general.approved_by');
        case 'pending':
          return this.$t('general.pending');
        case 'rejected':
          return this.$t('general.rejected');
        default:
          return this.$t('general.unknown');
      }
    },

    async deleteCompetency(competency) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (isConfirmed) {
        try {
          await ApiService.delete(`/api/user/competency-remove/${competency.id}`);
          await this.loadCompetencies();
          showSuccess(this.$t('general.successfully_deleted'), 3000);
        } catch (error) {
          console.error('Yetkinlik silinirken hata:', error);
          showError(this.$t('general.error_occurred_while_deleting_competency'), 3000);
        }
      }
    },

    addCompetency() {
      this.$router.push({ name: 'competencies.and.user.definitions' })
    }
  }
};
</script>

<style scoped>
/* Modal Overlay */
.competencies-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999999;
  overflow: hidden;
  touch-action: none;
}

/* Modal Dialog */
.competencies-modal-dialog {
  max-width: 600px !important;
  width: 90vw !important;
  max-height: 80vh !important;
  background: #fff !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18) !important;
  padding: 0 !important;
  margin: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  touch-action: auto;
}

/* Modal Header */
.competencies-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
}

.competencies-modal-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.competencies-modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.competencies-modal-close:hover {
  background-color: #f5f5f5;
}

/* Modal Body */
.competencies-modal-body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  max-height: calc(80vh - 140px);
  scroll-behavior: smooth;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
}

/* Modal Footer */
.competencies-modal-footer {
  display: flex;
  justify-content: center;
  padding: 20px 24px;
  border-top: 1px solid #e5e5e5;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  background: #fff;
  z-index: 1;
}

/* Tablo Stilleri */
.competencies-table {
  width: 100%;
}

.competencies-table .table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
}

.competencies-table .table thead th {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e5e5e5;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  text-align: left;
}

.competencies-table .table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.competencies-table .table tbody tr:hover {
  background-color: #f8f9fa;
}

.competencies-table .table tbody td {
  padding: 16px 24px;
  vertical-align: middle;
}

.competency-name {
  width: 60%;
}

.competency-title {
  font-weight: 500;
  color: #333;
  display: block;
}

.competency-status {
  text-align: center;
  width: 30%;
}

.type-badge {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
}

.type-badge-approved {
  background-color: #d4edda;
  color: #155724;
}

.type-badge-pending {
  background-color: #fff3cd;
  color: #856404;
}

.type-badge-rejected {
  background-color: #f8d7da;
  color: #721c24;
}

.type-badge-default {
  background-color: #e2e3e5;
  color: #383d41;
}

.competency-actions {
  text-align: center;
  width: 10%;
}

.delete-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background-color: #f8d7da;
  color: #721c24;
}

.add-competency-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-competency-btn:hover {
  background-color: #0056b3;
}

/* Responsive Tasarım */
@media (max-width: 600px) {
  .competencies-modal-dialog {
    max-width: 98vw !important;
    width: 98vw !important;
    max-height: 98vh !important;
  }

  .competencies-modal-header {
    padding: 16px;
    min-height: 60px;
  }

  .competencies-modal-body {
    max-height: calc(98vh - 180px);
  }

  .competencies-table .table thead th,
  .competencies-table .table tbody td {
    padding: 12px 16px;
  }

  .competency-title {
    font-size: 0.875rem;
  }

  .type-badge {
    font-size: 0.7rem;
    padding: 3px 8px;
  }

  .competencies-modal-footer {
    padding: 16px;
    min-height: 80px;
  }

  .add-competency-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .competencies-modal-dialog {
    max-height: 100vh !important;
    width: 100vw !important;
    max-width: 100vw !important;
    border-radius: 0 !important;
  }

  .competencies-modal-body {
    max-height: calc(100vh - 155px);
  }

  .competencies-modal-header {
    padding: 12px 16px;
    min-height: 50px;
  }

  .competencies-table .table thead th,
  .competencies-table .table tbody td {
    padding: 10px 12px;
  }

  .competency-title {
    font-size: 0.8rem;
  }

  .type-badge {
    font-size: 0.65rem;
    padding: 2px 6px;
  }

  .competencies-modal-footer {
    padding: 12px 16px;
    min-height: 70px;
  }
}


</style>
